<template>
	<v-container fluid class="relative py-0">
		<!-- create dialog -->
		<v-dialog v-model="dialog" width="500px" height="auto">
			<v-form ref="form" @submit.prevent="submitInvoice" :disabled="submitLoading">
				<v-card>
					<v-card-title>
						{{$t('invoices.add-invoice')}}
					</v-card-title>
					<v-card-text class="pb-0">
						<v-row dense>
							<!-- invoice category -->
							<v-col cols="auto" lg="6" v-if="useInvoiceCategories">
								<v-autocomplete
									v-model="submitData.categoryId"
									:label="$t('refunds.invoice-categories')"
									:items="invoiceCategories"
									item-text="name"
									item-value="id"
									outlined 
									dense
									clearable
									@change="suggestCategoryData()"
								></v-autocomplete>
							</v-col>

							<!-- config account -->
							<v-col cols="auto" lg="6" v-if="useConfigInvoicesAccount">
								<v-autocomplete
									outlined 
									dense
									:label="$t('refunds.buy-refund-config-account')"
									:items="tradeConfigAccounts"
									item-text="name"
									item-value="id"
									v-model="submitData.account_trade_config_id"
									clearable
								></v-autocomplete>
							</v-col>

							<!-- account -->
							<v-col cols="12" sm="6">
								<v-autocomplete
									v-model="submitData.account"
									:label="$t('inputs.account')"
									:items="configs.accounts.filter(c => c.type === 2)"
									item-value="id"
									:item-text="item => item.accounting_no + '-'+ item.name"
									outlined
									dense
									:rules="[rules.required]"
								>
									<template v-slot:selection="{ item }">
										{{ item.accounting_no +'-'+ item.name }}
									</template>
									<template v-slot:item="{ item }">
										{{ item.accounting_no + '-'+ item.name }}
										<v-spacer></v-spacer>
										<bee-advanced-money-label
											v-if="!isWarehousePermission"
											:with-color="true"
											:value="item?.account_statement_summary"
											:show-zeros-values="true"
										></bee-advanced-money-label>
									</template>
								</v-autocomplete>
							</v-col>

							<!-- refunded from -->
							<v-col cols="12" sm="6">
								<v-autocomplete
									v-model="submitData.refundedFrom"
									:items="buyInvoices"
									item-text="no"
									item-value="id"
									multiple
									:label="$t('inputs.refunded-from-invoice')"
									outlined
									dense
								/>
							</v-col>

							<!-- rfDoc -->
							<v-col cols="12" sm="6">
								<v-text-field 
									v-model="submitData.refDoc"
									:label="$t('inputs.ref-doc')"
									outlined
									dense
									:rules=" !canCreateInvoiceWithoutDocNo ? [rules.required] : []"
								/>
							</v-col>
				
							<!-- date -->
							<v-col cols="12" sm="6">
								<bee-date-input
									v-model="submitData.date"
									:label="$t('inputs.date')"
									dense
									clearable
									prepend-icon="mdi-calendar"
									user-menu-picker
									input-format-order="DD-MM-YYYY"
									:disabled="submitLoading"
									class="w-100"
									:rules="[
										rules.date,
										v => moment(v).format('YYYY-MM-DD') <= moment().format('YYYY-MM-DD') || canAddFutureDate
									]"
								></bee-date-input>
							</v-col>
							<v-col cols="12" lg="6" v-if="!isWarehousePermission && useChangeCurrency">
								<v-autocomplete
									v-model="submitData.alternative_currency_id"
									:items="configs.currencies"
									:label="currencyNameCreate"
									item-value="id"
									item-text="symbol"
									dense
									hide-details
									outlined
									class="rounded-lg"
									@change="recommendCurrencyPriceCreate(submitData.alternative_currency_id)"
								></v-autocomplete>
							</v-col>
							<v-col cols="12" lg="6" v-if="!isWarehousePermission && useChangeCurrency">
								<v-text-field
									v-model.number="submitData.currency_price"
									dense
									hide-details
									outlined
									type="number"
									:disabled="disableDefaultCurrencyPriceCreate"
									:label="$t('change-currency.currency-value')"
									class="rounded-lg"
								>
								</v-text-field>
							</v-col>
							<!-- note -->
							<v-col cols="12">
								<v-textarea
									v-model="submitData.notes"
									:label="$t('inputs.notes')"
									outlined
									auto-grow
									no-resize
									rows="2"
								/>
							</v-col>
						</v-row>
					</v-card-text>
					<v-card-actions>
						<v-btn
							type="submit"
							text
							:loading="submitLoading"
							color="success-color"
						>
							{{$t('buttons.create')}}
						</v-btn>
						<v-btn
							text
							color="danger-dark-color"
							:disabled="submitLoading"
							@click="dialog = false"
						>
							{{$t('buttons.cancel')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- dialog delete invoice -->
		<bee-dialog
			v-model="deleteDialog"
			:title="$t('invoices.delete-invoice')"
			:message="$t('invoices.delete-invoice-confirm')"
			max-width="450"
			@handler="deleteInvoice"
			@close="deleteDialog = false"
		></bee-dialog>

		<!-- dialog delete entry -->
		<bee-dialog
			v-model="dialogDeleteAccountingEntry"
			:title="$t('invoices.delete-entry')"
			:message="$t('invoices.are-you-sure-you-want-to-delete-the-entry')"
			max-width="300"
			@handler="deleteEntry()"
			@close="dialogDeleteAccountingEntry = false; deleteEntryId = null"
		></bee-dialog>
				<!-- change currency dialog -->
		<v-dialog
			v-model="currentCurrencyDialog"
			width="400"
			persistent
		>
			<v-card class="rounded-lg">
				<v-card-title>
					{{ $t('change-currency.change-invoice-currency') }}
				</v-card-title>
				<v-card-text class="pb-1 pt-2">
					<div>
						<v-row dense>
							<v-col cols="12" lg="6">
								<v-autocomplete
									v-model="invoiceData.alternativeCurrencyId"
									:items="configs.currencies"
									item-value="id"
									item-text="symbol"
									dense
									hide-details
									outlined
									:label="currencyNameUpdate"
									class="rounded-lg"
									@change="recommendCurrencyDefaultPriceUpdate(invoiceData.alternativeCurrencyId)"
								></v-autocomplete>
							</v-col>
							<v-col cols="12" lg="6">
								<v-text-field
									v-model.number="invoiceData.alternativeCurrencyPrice"
									dense
									hide-details
									outlined
									:disabled="disableDefaultCurrencyPrice"
									:label="$t('change-currency.currency-value')"
									class="rounded-lg"
									type="number"
								>
								</v-text-field>
							</v-col>
						</v-row>
					</div>
				</v-card-text>
				<v-card-actions>
					<v-btn
						color="transparent warning--text"
						depressed
						:loading="changeCurrencyLoading"
						@click="isUpdateInvoice = true ;submitInvoice()"
					>
						{{ $t('change') }}
					</v-btn>
					<v-btn
						@click="currentCurrencyDialog = false; resetCurrencyDialog()"
						color="transparent red--text"
						depressed
					>
						{{ $t('cancel') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	
		<!-- chang currency alert dialog -->
		<v-dialog 
			v-model="changeCurrencyAlertDialog"
			width="400"
			persistent
			
		>
			<v-card class="rounded-lg">
				<v-card-title>
					{{ $t('change-currency-alert.dialog-header') }}
				</v-card-title>
				<v-card-text class="pb-1">
					{{ $t('change-currency-alert.can-not-change-currency') }}
				</v-card-text>
				<v-card-actions>
					<v-btn
						@click="changeCurrencyAlertDialog = false"
						color="transparent grey--text"
						depressed
					>
						{{ $t('close') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="addSerialNumbersDialog" content-class="rounded-lg" max-width="550px" persistent>
			<v-card>
				<v-card-title>
					{{
						canAddSerialNumber? $t('InvoiceBuyPage.add-serial-number') : $t('InvoiceBuyPage.serial-numbers')
					}}
				</v-card-title>
				<v-card-text>
					<bee-handy-smart-table
						ref="serialNumberTable"
						:headers="serialNumberHeaders"
						:items="canAddSerialNumber ? tableInputs['serial-number'] || [] : serialNumbers"
						v-model="serialNumber"
						zebra
						pagination-on-scroll
						hide-inputs-details
						hide-no-data-area
						with-columns-divider
						stay-in-input-fields
						shown-rows-text=""
						:show-add-row="canAddSerialNumber"
						divided-column
						@keydown.enter="
							resetSerialNumberValue();
						
						"              
					>
						<template v-slot:input.value="{attr}">
							<v-combobox
								v-model="serialNumber.value"
								v-bind="attr"
								autofocus
								:items="materialSerialNumbers.filter(v => !tableInputs['serial-number']?.find(c => c.value === v))"
								class="text-subtitle-2"
								dense
								hide-details
								@change="checkMaterialSerialNumber(tableInputs.materialId, serialNumber.value)"     
								@keydown.enter="resetSerialNumberValue"              
							>
								<template v-slot:item="{ item, on, attrs }" >
									<v-list-item v-on="on" v-bind="attrs" class="text-subtitle-2" dense @click="resetSerialNumberValue">
										{{ item }}
									</v-list-item>
								</template>
								<template v-slot:selection="{item}">
									{{ item }}
								</template>
							</v-combobox>
						</template>
						<template v-slot:item.actions="{index}">
							<v-btn v-if="canAddSerialNumber" small icon  class="ms-2 pa-0" color="red" @click="tableInputs['serial-number'].splice(index, 1)">
								<v-icon>mdi-delete</v-icon>
							</v-btn>
						</template>
					</bee-handy-smart-table>
				</v-card-text>
				<v-card-actions class="pt-0">
					<v-btn
						text
						color="red"
						@click="canAddSerialNumber = false;addSerialNumbersDialog = false"
					>
						{{ $t('buttons.cancel') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="serialNumberAlertDialog" content-class="rounded-lg" max-width="550">
			<v-card>
				<v-card-title>
					{{ $t('InvoiceBuyPage.choose-serial-number-alert') }}
				</v-card-title>
				<v-card-text class="pb-2 pt-2">
					{{ $t('headers.serial-number') }}
					:
					{{ serialNumberAlertText }}
					<br>
					{{ $t('InvoiceBuyPage.choose-serial-number-alert-content') }}
				</v-card-text>
				<v-card-actions class="pt-1">
					<v-btn
						@click="serialNumberAlertDialog = false"
						color="transparent"
						class="green--text"
						text
					>
						{{ $t('buttons.ok') }}
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<!-- title & buttons -->
		<v-row class="ma-0 align-center">
			<v-col cols="auto"  align="center" class="d-flex justify-start">
				<slot name="breadcrumbs" :item="true"></slot>
			</v-col>

			<v-col
				v-if="useInvoiceCategories"
				md="2"
				sm="3"
				cols="12"
			>
				<v-autocomplete
					v-model="selectedInvoiceCategory"
					:label="$t('inputs.invoice-category')"
					:items="allInvoiceCategories"
					item-text="name"
					item-value="id"
					:disabled="loading"
					:item-disabled="item => item.id && !buyRefundInvoices.filter(c => c.category_id === item.id).length"
					outlined 
					dense
					hide-details
					@change="$router.push({name:'buy-invoice-refund', query: {invoiceCategory: selectedInvoiceCategory}})"
				/>
			</v-col>

			<v-col cols="auto" class="d-flex flex-row justify-start">
				<bee-bond-up-down
					v-model="invoiceNo"
					:items="
						!$route.query.invoiceCategory
							? buyRefundInvoices.map(c => c.no)
							: matchedCategoryInvoices.map(c => c.sub_no)
					"
					class="medium-placeholder"
					:label="$t('invoices.invoice-no')"
					:disabled="loading || !buyRefundInvoices.length || loading"
					@keydown-enter-prevent="invoiceNoIsChanged"
					@programmatically-changed="invoiceNoIsChanged"
				></bee-bond-up-down>
			</v-col>

			<v-col cols="auto">
				<v-btn
					v-if="createInvoicePermission"
					color="success white--text"
					:disabled="isViewer || loading"
					class="mx-2"
					@click="dialog = true"
				>
					<v-icon> mdi-plus </v-icon>
					{{$t('buttons.new')}}
				</v-btn>

				<v-btn
					v-if="deleteInvoicePermission && $route.params.id"
					:disabled="isViewer || loading"
					color="danger-color"
					dark
					@click="deleteDialog = true">
					<v-icon>
					mdi-delete
					</v-icon>
					{{$t('buttons.delete')}}
					</v-btn
				>
			</v-col>

			<v-spacer></v-spacer>
			<v-col cols="auto" v-if="!isAccountantPermission && !isViewer">
				<v-tooltip right>
					<template v-slot:activator="{on, attrs}">
						<div
							v-on="on"
							v-bind="attrs"
						>
							<v-btn
								icon
								:loading="materialsLoading"
								:disabled="loading"
								@click="fetchMaterials()"
							>
								<v-icon  color="primary" size="18">
									fa-sync
								</v-icon>
							</v-btn>
						</div>
					</template>
					{{$t('update-materials')}}
				</v-tooltip>
			</v-col>
			<v-col v-if="useInvoicesAuditLogs" cols="auto" class="px-0">
				<v-tooltip bottom>
					<template v-slot:activator="{ on, attr }">
						<v-btn
							small
							depressed
							color="transparent"
							:disabled="loading"
							@click="infoDialog = true"
							v-on="on"
							v-bind="attr"
						>
						<v-icon>mdi-information-outline</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('invoice-info') }}</span>
				</v-tooltip>
			</v-col>
			<!-- invoice posting progress circular -->
			<v-col cols="auto" class="d-flex justify-lg-end py-1">
				<router-link style="text-decoration: none;" :to="{name: 'invoices', query: { type: 3, status: postingPattern? 5 : 2 }}">
					<bee-progress-circular
						:size="60"
						:width="6"
						:title="bond_posting_ratio_caption"
						:value="bond_posting_ratio"
						:tooltip="bond_posting_ratio_tooltip"
					></bee-progress-circular>
				</router-link>
			</v-col>
			<v-col cols="auto" class="pe-0">
				<v-menu offset-y>
					<template v-slot:activator="{ attr, on }">
						<v-btn dark color="alert-color" v-on="on" v-bind="attr">
							<v-icon class="me-1">mdi-export-variant</v-icon>
							{{$t('exports.export')}}
						</v-btn>
					</template>
	
					<v-list nav dense>
						<v-list-item @click="getReport('pdf')">
							<v-list-item-icon class="me-4">
								<v-icon>mdi-printer</v-icon>
							</v-list-item-icon>
							<v-list-item-title>
								{{$t('exports.export-as-pdf')}}
							</v-list-item-title>
						</v-list-item>
						<v-list-item @click="getReport('excel')">
							<v-list-item-icon class="me-4">
								<v-icon>mdi-microsoft-excel</v-icon>
							</v-list-item-icon>
							<v-list-item-title>
								{{$t('exports.export-as-excel')}}
							</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-menu>
			</v-col>
		</v-row>
		
		<v-row
			class="border-container ma-0 py-0 px-0"
			align="center"
		>
			<!-- account -->
			<v-col cols="6" md="2">
				<v-autocomplete
					v-model="invoiceData.accountId"
					:label="$t('inputs.account')"
					:items="configs.accounts.filter(c => c.type === 2)"
					item-value="id"
					:item-text="item => item.accounting_no + '-'+ item.name"
					outlined
					dense
					hide-details
					:disabled="postingPermission || loading"
					:rules="[rules.required]"
					@change="$event ? (() => { isUpdateInvoice = true; submitInvoice(); })() : null"
				>
					<template v-slot:selection="{ item }">
						{{ item.accounting_no +'-'+ item.name }}
					</template>
					<template v-slot:item="{ item }">
						{{ item.accounting_no + '-'+ item.name }}
						<v-spacer></v-spacer>
						<bee-advanced-money-label
							v-if="!isWarehousePermission"
							:with-color="true"
							:value="item?.account_statement_summary"
							:show-zeros-values="true"
						></bee-advanced-money-label>
					</template>
				</v-autocomplete>
			</v-col>

			<!-- date -->
			<v-col cols="6" md="2">
				<bee-date-input
					v-model="invoiceData.date"
					:label="$t('inputs.date')"
					dense
					hide-details
					prepend-icon="mdi-calendar"
					:user-menu-picker="!postingPermission"
					:rules="[
						rules.date,
						v => moment(v).format('YYYY-MM-DD') <= moment().format('YYYY-MM-DD') || canAddFutureDate
					]"
					input-format-order="DD-MM-YYYY"
					class="w-100"
					:disabled="postingPermission || loading"
					@change="isUpdateInvoice = true; submitInvoice();"
				></bee-date-input>
			</v-col>

			<!-- warehouse -->
			<v-col cols="6" md="2">
				<v-autocomplete
					v-model="tableInputs.warehouse"
					:items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? warehousesForKeeper : warehouses"
					:item-text="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_name' : 'name'"
					:item-value="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id'"
					outlined
					dense
					:label="$t('inputs.warehouse')"
					hide-details
					:disabled="postingPermission || loading"
				/>
			</v-col>

			<!-- ref doc -->
			<v-col cols="6" md="1">
				<v-text-field
					v-model="invoiceData.refDoc"
					outlined
					dense
					:label="$t('inputs.ref-doc')"
					hide-details
					:disabled="postingPermission || loading"
					:rules="!canCreateInvoiceWithoutDocNo ? [rules.required] : []"
					@change="isUpdateInvoice = true; submitInvoice();"
				/>
			</v-col>
			<v-col cols="12" sm="auto" xs="auto" md="auto" lg="auto" class="d-flex align-center justify-center px-1" v-if="!isWarehousePermission && useChangeCurrency">
				<v-tooltip bottom content-class="rounded-lg">
					<template v-slot:activator="{ on, attr }">
						<v-btn
							v-bind="attr"
							v-on="on"
							icon
							:disabled="postingPermission || loading"
							@click="currentCurrencyDialog = true;"
						>
							<v-icon>
								mdi-cash-multiple
							</v-icon>
						</v-btn>
					</template>
					<span>{{ $t('change-currency.change-invoice-currency') }}</span>
				</v-tooltip>
				<span class="grey--text text--darken-2" v-if="!isWarehousePermission && useChangeCurrency">
					{{configs.currencies.find(c => c.id === (currentInvoice.alternative_currency_id ? currentInvoice.alternative_currency_id : currentFinancialFile.default_currency_id)).symbol}}
				</span>
			</v-col>
			<v-spacer />

			<!-- net invoice -->
			<!-- <v-col
				v-if="!isWarehousePermission"
				cols="auto"
				class="pb-lg-0 pt-lg-1 d-flex flex-column"
				align-self="stretch"
			>
				<bee-outlined-header-info
					:head="$t('invoices.net-invoice')"
					style="line-height: 1.5rem;"
					border-color="transparent"
					class="pa-lg-0"
					:column="$vuetify.breakpoint.lgAndUp"
				>
					<template  v-slot:content="{}">
						<bee-money-label number="" type="credit" :withColor="true" empty-state="_"></bee-money-label>
					</template>
				</bee-outlined-header-info>
			</v-col> -->

			<!-- save button -->
			<v-col
			v-if="isAdmin&& postingPattern !== postingPriorityType.both && postingButtonStatus"
			cols="auto"
			sm="auto"
			xs="auto"
			md="auto"
			lg="auto"
			class="d-flex flex-md-column justify-center py-lg-0 ps-lg-0"
		>
			<v-btn context="invoice"
				class="white--text fs-6 mx-1 d-flex align-center"
				:class="{'px-6 py-3 mb-1': $vuetify.breakpoint.mdAndUp}"
				:disabled="!$route.params.id || loading || isViewer|| secondaryPostingButtonLoading"
				:loading="primaryPostingButtonLoading" 
				:color="adminPostingTypeActions.primaryButton.color" 
				@click="
						(
							(
								(
									((!tableInputs.materialId || !tableInputs.quantity || (!useZeroInvoiceEntiresPrice ? !tableInputs.price : false))) 
									&& !((!tableInputs.materialId && !tableInputs.quantity && (useZeroInvoiceEntiresPrice ? !tableInputs.price : true)) && tableInputs.warehouse)
								)
								&& !postingPermission
							) 
							? confirmPostDialog = true 
							: adminPostingTypeActions.primaryButton.action()
						)
					"
			>
				<v-icon small>
					{{ adminPostingTypeActions.primaryButton.icon }}
				</v-icon>
				{{adminPostingTypeActions.primaryButton.text}}
			</v-btn>
			<v-btn context="invoice"
				class="white--text fs-6 mx-1 d-flex align-center"
				:class="{'px-6 py-3': $vuetify.breakpoint.mdAndUp}"
				:disabled="!$route.params.id || loading || isViewer|| primaryPostingButtonLoading"
				:loading="secondaryPostingButtonLoading" 
				:color="adminPostingTypeActions.secondaryButton.color" 
				@click="
						(
							(
								(
									((!tableInputs.materialId || !tableInputs.quantity || (!useZeroInvoiceEntiresPrice ? !tableInputs.price : false))) 
									&& !((!tableInputs.materialId && !tableInputs.quantity && (useZeroInvoiceEntiresPrice ? !tableInputs.price : true)) && tableInputs.warehouse)
								)
								&& !postingPermission
							) 
							? confirmPostDialog = true 
							: adminPostingTypeActions.secondaryButton.action()
						)
					"
			>
				<v-icon small>
					{{ adminPostingTypeActions.secondaryButton.icon }}
				</v-icon>
				{{ adminPostingTypeActions.secondaryButton.text }}
			</v-btn>
		</v-col>
		<v-col
			v-if="!(isAdmin&& postingPattern !== postingPriorityType.both) && postingButtonStatus"
			cols="auto"
			class="d-flex flex-column align-items-center py-0 ps-0"
			:align-self="$vuetify.breakpoint.mdAndDown ? 'center' : 'stretch'"
		>			
			<v-btn
				v-if="
					useUnPostingPermission
						? (!postingPermission || (postingPermission && canUnPost))
						: true
				"
				:color="postingPermission ? 'deletingColor' : 'blueGray'" 
				:height="$vuetify.breakpoint.mdAndUp ? '100%' : 35"
				class="white--text"
				:class="{'px-6 py-3': $vuetify.breakpoint.mdAndUp}"
				:disabled="!$route.params.id || loading || isViewer"
				@click="
					(
						(
							((!tableInputs.materialId || !tableInputs.quantity || (!useZeroInvoiceEntiresPrice ? !tableInputs.price : false))) 
							&& !((!tableInputs.materialId && !tableInputs.quantity && (useZeroInvoiceEntiresPrice ? !tableInputs.price : true)) && tableInputs.warehouse)
						)
						&& !postingPermission
					) 
						? confirmPostDialog = true 
						: changeInvoiceState()
				"
			>
				<v-icon small class="me-1">
					{{!postingPermission ? 'fa-envelope-open' : 'fa-envelope'}}
				</v-icon>
				{{!postingPermission ? $t('buttons.save') : $t('buttons.edit')}}
			</v-btn>
		</v-col>
			<!-- <v-col
				v-if="postingButtonStatus"
				cols="auto"
				class="d-flex flex-column align-items-center py-0 ps-0"
				:align-self="$vuetify.breakpoint.mdAndDown ? 'center' : 'stretch'"
			>
				<v-btn
					v-if="
						useUnPostingPermission
							? (!postingPermission || (postingPermission && canUnPost))
							: true
					"
					:color="!postingPermission ? 'success' : 'edit-color white--text'"
					:height="$vuetify.breakpoint.lgAndUp ? '100%' : 35"
					:class="{'px-6 py-3': $vuetify.breakpoint.lgAndUp}"
					:disabled="!$route.params.id || loading"
					@click="
						(
							(
								((!tableInputs.materialId || !tableInputs.quantity || (!useZeroInvoiceEntiresPrice ? !tableInputs.price : false))) 
								&& !((!tableInputs.materialId && !tableInputs.quantity && (useZeroInvoiceEntiresPrice ? !tableInputs.price : true)) && tableInputs.warehouse)
							)
							&& !postingPermission
						) 
							? confirmPostDialog = true 
							: changeInvoiceState()
					"
				>
					<v-icon size="20" class="me-1">
						{{!postingPermission ? 'mdi-check' : 'mdi-pencil'}}
					</v-icon>
					{{!postingPermission ? $t('buttons.save') : $t('buttons.edit')}}
				</v-btn>
			</v-col> -->

			<!-- post state -->
			<v-col 
				cols="12"
				sm="12"
				md="auto"
				class="py-0 px-0"
				align-self="stretch"
			>
				<div
					class="d-flex align-center justify-center px-4 white--text rounded-lg h-100"
					:class="[
						$vuetify.breakpoint.lgAndUp?'py-2': '' ,
						currentInvoice.posting_type === postingType.materialsJournal
						||currentInvoice.posting_type === postingType.bond ? 'yellow darken-4' :  postingPermission ? 'postedColor' : 'unpostedColor',
					]"
				>
					{{ currentInvoice.posting_type === postingType.materialsJournal 
						?$t('invoices.warehouse-posted'):
						currentInvoice.posting_type === postingType.bond ?
						$t('invoices.posted-for-accounting')
						:postingPermission 
							? $t('invoices.posted')
							: $t('invoices.non-posted')
					}}
				</div>
			</v-col>
		</v-row>
			
		<!-- table -->
		<v-card-text class="px-0 pt-2 pb-0">
			<bee-handy-smart-table
				ref="table"
				v-model="tableInputs"
				:headers="headers"
				:items="entries"
				:items-per-page="20"
				pagination-on-scroll
				:loading="loading"
				:data-table-props="{ disableSort: true }"
				fixed-header
				hide-no-data-area
				with-columns-divider
				dense
				:height="$vuetify.breakpoint.lgAndUp ? $vuetify.breakpoint.height - 305 :'65vh'"
				stay-in-input-fields
				:show-add-row="!postingPermission && Boolean($route.params.id) && !isViewer && showAddRowPermission"
				:editable-rows="!submitLoading && !loading"
				hide-inputs-details
				:context-menu-items="contextMenuItems"
				disable-mobile-context-menu
				:scroll-to-row-expression="(item) => item.id === entryId || item.id === entries[entries.length - 1].id"
				zebra
				hot-inputs
				:validation-messages.sync="validationMessages"
				:shown-rows-text="$t('shown-rows-text')"
				:disable-context-menu="submitLoading || loading || postingPermission"
				:disable-input-fields="submitLoading || postingPermission"
				@edit-cancelled="entryId = null"
				@context-menu-edit="setRowIndex"
				@context-menu-delete="({ item }) => { deleteEntryId = item.id; dialogDeleteAccountingEntry = true }"
				@keypress.enter="saveEntry"
				@reached-last-input="!saveInvoiceWithLastEntry ? saveEntry() : null"
			>
				<!-- warehouse -->
				<template v-slot:input.warehouse="{ on, attr }">
					<v-autocomplete
						v-on="on"
						v-bind="attr"
						class="text-subtitle-2"
						:items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? warehousesForKeeper : warehouses"
						:item-text="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_name' : 'name'"
						:item-value="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? 'warehouse_id' : 'id'"
					/>
				</template>

				<!-- materialId -->
				<template v-slot:input.materialId="{ on, attr }">
					<v-autocomplete
						v-on="on"
						v-bind="attr"
						class="text-subtitle-2"
						:items="userTypePermission === userType.warehouseKeeper && multipleWarehouses ? items.filter(c => c.warehouse_id === tableInputs.warehouse && c.type === 2) : items.filter(c => c.type === 2)"
						item-text="name"
						item-value="id"
						@change="recommendMaterialData()"
					>
						<template v-slot:item="{item}">
							{{item.no + '-'+ item.name }}
							<v-spacer></v-spacer>
							<bee-material-amount-label
								:with-color="true"
								:show-zeros-values="true"
								:value="item.balance"
							></bee-material-amount-label>
						</template>
					</v-autocomplete>
				</template>

				<!-- delivery status -->
				<template v-slot:input.delivery_status_id="{ attr, on }">
					<v-autocomplete
						v-on="on"
						v-bind="attr"
						class="text-subtitle-2"
						:items="deliveryStatus.filter(c => c.invoice_type === 3)"
						item-text="name"
						item-value="id"
						dense
						hide-details
					>
					</v-autocomplete>
				</template>

				<!-- actions -->
				<template v-slot:item.actions="{ index }">
					<tooltip :text="$t('buttons.delete')" content-class="me-2">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							color="danger-color white--text"
							class="px-1 mx-1"
							@click="deleteEntryId = item.id; dialogDeleteAccountingEntry = true"
						>
							<v-icon size="22">mdi-delete</v-icon>
						</v-btn>
					</tooltip>
					
					<!-- edit -->
					<tooltip :text="$t('buttons.edit')" content-class="me-2">
						<v-btn
							depressed
							height="30"
							width="30"
							min-width="30"
							color="edit-color white--text"
							class="px-1"
							@click="$refs.table.activeEditForRow(index); setRowIndex({ index })"
						>
							<v-icon size="22"> mdi-pencil </v-icon>
						</v-btn>
					</tooltip>
				</template>
				<template v-slot:item.serial-number="{item}">
					<div class="d-flex align-center justify-center text-subtitle-2">
						<v-btn v-if="item.serial_number?.length" text :ripple="false" class="" @click.stop="serialNumbers = item.serial_number ;addSerialNumbersDialog = true">
							<span class=" greyDarken2--text me-1">
								{{ 
									item.serial_number?.length || null
								}}
							</span>
							<v-icon small>
								mdi-script-text-outline
							</v-icon>
						</v-btn>
						<span v-else>-</span>
					</div>
				</template>
				<template v-slot:input.serial-number="{on, attr}">
					<v-combobox
						v-model="serialNumber.value"
						v-bind="attr"
						:items="materialSerialNumbers.filter(v => !tableInputs['serial-number']?.find(c => c.value === v))"
						class="text-subtitle-2"
						dense
						hide-details
						@change="checkMaterialSerialNumber(tableInputs.materialId, serialNumber.value)"     
						@keydown.enter="resetSerialNumberValue"              
					>
						<template v-slot:append>
							<span class="mt-2 me-1 greyDarken2--text">
								{{ 
								tableInputs['serial-number']?.length || null
								}}
							</span>
							<v-icon small  class="mt-2" @click.stop.prevent="$refs['input-serial-number'].isMenuActive = false;canAddSerialNumber = true;addSerialNumbersDialog = true;">
								mdi-script-text-outline
							</v-icon>
						</template>
						<template v-slot:item="{ item, on, attrs }" >
							<v-list-item v-on="on" v-bind="attrs" class="text-subtitle-2" dense @click="resetSerialNumberValue">
								{{ item }}
							</v-list-item>
						</template>
						<template v-slot:selection="{item}">
							{{ item }}
						</template>
					</v-combobox>
				</template>
				<!-- hint -->
				<template v-slot:no-input.hint>
					<v-menu bottom v-if="validationMessages.length > 0">
						<template v-slot:activator="{ on, attr }">
							<v-icon
								v-on="on"
								v-bind="attr"
								color="danger-color"
								size="20"
							>mdi-information</v-icon>
						</template>

						<v-list>
							<v-list-item
								v-for="(message, i) in inputsValidationMessages"
								:key="i"
								dense
								class="px-2"
							>
								<v-list-item-icon class="me-0 mb-0">
									<v-icon color="danger-color" x-small>mdi-circle-half</v-icon>
								</v-list-item-icon>
								<v-list-item-title class="danger-color--text">
									{{message}}
								</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</template>
				<template v-slot:pagination-on-scroll.page-text="{shownItemsLength, totalItems}">
					<v-row dense>
						<v-col 
							cols="auto" 
							class="px-1" 
							:style="{ marginRight: ($vuetify.breakpoint.width - $vuetify.breakpoint.width / 2 - 290) + 'px' }"
						>
							<span>{{ $t('total-quantities') }} {{ totalQuantities?.toLocaleString('en') }} </span>
						</v-col>
						<v-col 
							cols="auto" 
							class="px-1"
							:style="{
								marginRight: ($vuetify.breakpoint.width / 4 - 310) + 'px',
							}"
						>
							<span>
								{{ $t('total-counts') }} {{ totalCounts?.toLocaleString('en') }} 
							</span>
						</v-col>
						<v-spacer></v-spacer>
						<v-col cols="auto" class="me-5">
							<span>
								{{shownItemsLength}} / {{totalItems}}
							</span>
						</v-col>
					</v-row>
				</template>

				<template v-slot:item.hint="{ item, index }">
					<div class="text-center">
						<v-menu offset-y>
							<template v-slot:activator="{ on, attr }">
								<v-btn
									small
									icon
									v-bind="attr"
									v-on="on"
									width="32"
								>
									<v-icon>
										mdi-dots-vertical
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item @click="$refs.table.activeEditForRow(index); setRowIndex({ item })">
									<v-list-item-content class="pa-0">
										{{ $t('buttons.edit') }}
									</v-list-item-content>
								</v-list-item>
								<v-list-item
									v-if="deleteEntryPermission"
									@click="deleteEntryId = item.id; dialogDeleteAccountingEntry = true"
								>
									<v-list-item-content class="pa-0">
										{{ $t('buttons.delete') }}
									</v-list-item-content>
								</v-list-item>
							</v-list>
						</v-menu>
					</div>
				</template>
			</bee-handy-smart-table>
		</v-card-text>

		<!-- footer -->
		<v-footer app height="60" class="px-3">
			<v-row class="ma-0" align="center">            
				<v-col lg="4" md="auto" class="pt-0 pb-1">
					<v-text-field
						v-model="invoiceData.notes"
                    	:label="$t('InvoiceSalePage.notes')"
						outlined
						hide-details
						dense
						:disabled="postingPermission || loading"
						@change="isUpdateInvoice = true; submitInvoice();"
                	></v-text-field>
				</v-col>
				<v-col v-if="useConfigInvoicesAccount" cols="auto" lg="2" class="pb-1 pt-0" >
					<v-autocomplete
						outlined 
						dense
						hide-details
						:disabled="postingPermission || loading"
						@change="isUpdateInvoice = true; submitInvoice();"
						:label="$t('refunds.buy-refund-config-account')"
						:items="tradeConfigAccounts"
						item-text="name"
						item-value="id"
						v-model="invoiceData.account_trade_config_id"
						clearable
					></v-autocomplete>
				</v-col>
				<v-col v-if="useInvoiceCategories && invoiceData.categoryId" cols="auto" lg="2" class="pb-1 pt-0">
					<v-autocomplete
						outlined 
						dense
						hide-details
						disabled
						:label="$t('refunds.invoice-categories')"
						:items="invoiceCategories"
						item-text="name"
						item-value="id"
						v-model="invoiceData.categoryId"
						clearable
					></v-autocomplete>
				</v-col>
				<v-spacer></v-spacer>

				<v-col v-if="!isWarehousePermission" cols="auto" class="d-flex align-center">
					<!-- <bee-outlined-header-info
						:head="$t('invoices.total')"
						border-color="transparent"
						class="pa-lg-0 me-4"
					>
						<template v-slot:content="{}">
							<bee-money-label
								:number="invoiceData.calculated_total_amount"
								type="credit"
								:withColor="true"
								empty-state="_"
							></bee-money-label>
						</template>
					</bee-outlined-header-info> -->

					<bee-outlined-header-info
						:head="$t('invoices.net-invoice')"
						border-color="transparent"
						class="pa-lg-0"
					>
						<template v-slot:content="{}">
							<bee-money-label
								:number="invoiceData.total_net_amount"
								type="credit"
								:withColor="true"
								empty-state="_"
							></bee-money-label>
						</template>
					</bee-outlined-header-info>
				</v-col>
			</v-row>
		</v-footer>

		<!-- the code of the confirm dialog goes here -->
		<v-dialog
			v-model="confirmPostDialog"
			max-width="500"
		>
			<v-card class="rounded-lg">
				<v-card-title>
					{{ $t('dialog-content.confirm-dialog-title') }}
				</v-card-title>
				<v-card-text>
					{{ $t('dialog-content.confirm-dialog-content') }}
				</v-card-text>
				<v-card-actions>
					<v-btn
						@click="changeInvoiceState()"
						color="green--text transparent"
						depressed
						:loading="submitLoading"
					>
					{{ $t('dialog-content.confirm-dialog-actions.confirm') }}
				</v-btn>
					<v-btn
						@click="confirmPostDialog = false"
						color="red--text transparent"
						depressed
						:disabled="submitLoading === true"
					>
					{{ $t('dialog-content.confirm-dialog-actions.cancel') }}
				</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- the code of the info dialog goes here -->
        <v-dialog
            v-model="infoDialog"
            max-width="550"
            content-class="rounded-xl"
            persistent
        >
            <v-card
                class="rounded-xl"
            >
                <v-card-title class="rounded-xl">{{ $t('invoice-info') }}</v-card-title>
                <v-card-text class="pb-1">
                    <bee-handy-table
                        :headers="infoHeaders"
                        :items="auditLogEntries"
                        dense
						pagination-on-scroll
                        shown-rows-text=""
                        fixed-header
                        elevation="1"
                        zebra
                    >
                        <template v-slot:item.type="{ item }">
                            <span  
								:class="
                                    item.type === actionType.create ? 'green--text'
                                    : item.type === actionType.post ? 'blue--text' 
                                    : item.type === actionType.unPost ? 'warning--text' 
                                    : null"
                            >
                                {{ actionTypes.find(c => c.type === item.type)?.name }}
                                <!-- {{
                                    actionTypes
                                        .slice()
                                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                                        .find(c => c.type === item.type)?.name
                                }} -->
                            </span>
                        </template>
                        <template v-slot:item.date="{ item }">
                            <div dir="auto" class="d-flex justify-end">
								{{ moment(item.date_formatted).locale('en').format('h:mm:ss A ,YYYY-MM-DD') }}
							</div>
                        </template>
                    </bee-handy-table>
                </v-card-text>
                <v-card-actions class="rounded-xl">
                    <v-btn 
                        color="transparent grey--text"
                        smal
                        depressed
                        @click="infoDialog = false"
                    >
                        <span>{{ $t('close') }}</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
	</v-container>
</template>

<script>
import rules from '@/validation'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import { userType, invoiceType, bondType, type, finalType, actionTypes, actionType, userPermission, postingType, postingPriorityType } from '@/helpers/enums';
import { multipleWarehouses, isMangeUnitAndRegionAndPatchAndLot, separationOfInvoices, useConfigInvoicesAccount, useUnPostingPermission, useInvoiceCategories, useZeroInvoiceEntiresPrice, canCreateInvoiceWithoutDocNo, useMaterialStatusDeliver, useChangeCurrency, useInvoicesAuditLogs } from '@/configs/global'
import ls from "@/settings/localStorageExtension";
import EventBus from '@/event-bus'

// FIXME use user warehouses
// const multipleWarehouses = false;

export default {
	data() {
		return {
			submitLoading: false,
			loading: false,

			dialog: false,
			
			validationMessages: [],

			// invoice data
			currentInvoice: {},
			invoiceNo: null,
			invoiceData: {
				accountId: null,
				date: null,
				refDoc: null,
				notes: null,
				refundedFrom: null,
				account_trade_config_id: null,
				categoryId: null,
				total_net_amount: null,
				calculated_total_amount: null,
				alternativeCurrencyId: null,
				alternativeCurrencyPrice: null,
			},
			isUpdateInvoice: false,

			submitData: {
				date: null,
				account: null,
				refDoc: null,
				notes: null,
				refundedFrom: [],
				account_trade_config_id: null,
				categoryId: null,
				alternative_currency_id: null,
				currency_price: null,
			},
			useChangeCurrency,
			// entires
			entryId: null,
			entries: [],
			auditLogEntries: [],
			tableInputs: {},
			
			rowEditIndex: null,
			deleteDialog: false,

			deleteEntryId: null,
			dialogDeleteAccountingEntry: false,
			rules: rules.rules,
			userType,
			invoiceType,
			useInvoicesAuditLogs,
			multipleWarehouses,
			isMangeUnitAndRegionAndPatchAndLot,
			separationOfInvoices,
			useConfigInvoicesAccount,
			useUnPostingPermission,
			useInvoiceCategories,
			canCreateInvoiceWithoutDocNo,
			useZeroInvoiceEntiresPrice,

			selectedInvoiceCategory: null,
			useMaterialStatusDeliver,
			postingPriorityType,
			confirmPostDialog: false,

			infoDialog: false,
			actionTypes,
			actionType,
			postingType,
			moment,
			primaryPostingButtonLoading: false,
			secondaryPostingButtonLoading  :false,
			currentCurrencyDialog: false,
			changeCurrencyAlertDialog: false,
			alternativeDiscountTotal: {},
			changeCurrencyLoading: false,
			change_payment_type: null,
			calculated_total_amount: null,
			materialSerialNumbers: [],
			serialNumberAlertDialog: false,
			serialNumberAlertText: null,
			addSerialNumber: false,
			serialNumber: {},
			addSerialNumbersDialog: false,
			canAddSerialNumber: false,
			serialNumbers: [],
			materialsLoading: false
		}
	},

	watch: {
		'$route'(val, oldVal) {
			this.selectedInvoiceCategory = val.query.invoiceCategory || null;
			if (val.params.id) {
				this.$refs.table.resetInputs({
					warehouse: this.configs.warehouse.id
				});
				this.fetchInvoice();
			} else if(oldVal.params.id && this.buyRefundInvoices.find(v => v.id === +oldVal.params.id)) {
				const latestMatchedInvoiceId = this.matchedCategoryInvoices[this.matchedCategoryInvoices.length - 1]?.id;
				this.$router.push({name: 'buy-invoice-refund', params: {id: latestMatchedInvoiceId}, query: val.query})

			} else{
				this.invoiceNo = null;
				this.invoiceData.accountId = null;
				this.invoiceData.date = null;
				this.invoiceData.notes = null;
				this.invoiceData.refDoc = null;
				this.invoiceData.refundedFrom = [];
				this.loading = false;
				}
		},

		dialog(val) {
			if (val) {
				this.submitData.date = moment().format('YYYY-MM-DD');
				let categoryId;

				if (this.$route.query.invoiceCategory) {
					categoryId = this.$route.query.invoiceCategory;
				} else {
					categoryId = this.defaultInvoiceCategories
						.find(c => c.invoice_type === invoiceType.buyRefund)?.category_id;
				}

				if (useInvoiceCategories && categoryId) {
					const invoiceCategory = this.getInvoiceCategoryById(categoryId);
					this.submitData.categoryId = categoryId;
					this.submitData.account_trade_config_id = invoiceCategory.purchases_refund_account_id;
					this.submitData.account = invoiceCategory.customer_account_id
				}
			} else {
				this.$refs.form.reset();
			}
		},

		/*selectedInvoiceCategory(val) {
            if (this.matchedCategoryInvoices.length && useInvoiceCategories) {
                const latestMatchedInvoiceId =
                    this.matchedCategoryInvoices[this.matchedCategoryInvoices.length - 1].id
                let query = val ? { invoiceCategory: val } : {};
				if(JSON.stringify(this.$route.query) !== JSON.stringify(query) || latestMatchedInvoiceId !== +this.$route.params.id) {
					this.$router.replace({
						name: this.$route.name,
						params: { id: latestMatchedInvoiceId },
						query
					})
				}
            }
        }*/
	},

	computed: {
		...mapState({
			currentFinancialFile: state => state.financialFiles.current_financial_file,
			materials: state => state.materials.materials,
			buyInvoices: state => state.invoices.simplified_buy_invoices,
			buyRefundInvoices: state => state.invoices.buyRefundInvoices,
			warehouses: state => state.warehouses.warehouses,
			configs: state => state.configs.configs,
			defaultFinancialFileAccounts: state => state.configs.default_financial_file_accounts,
			userTypePermission: state => state.permissions.user_type,
			numOfNotPostedInvoices: state => state.configs.configs.num_of_not_posted_invoices.filter(c => c.invoice_type === invoiceType.buyRefund),
			numOfTotalSaleRefundInvoices: state => state.configs.configs.num_of_total_invoices.filter(c => c.invoice_type === invoiceType.buyRefund),
			// warehousesForKeeper: state => state.warehouses.warehousesForKeeper,
			warehousesForKeeper: state => state.permissions.warehousesForKeeper,
			canUnPost: state => state.permissions.selfPermissions.canUnPost,
			invoiceCategories: state =>
				state.invoiceCategories.invoiceCategories
					.filter(c => c.category_invoice_types.map(c => c.invoice_type).includes(invoiceType.buyRefund)),
			defaultInvoiceCategories: state => state.invoiceCategories.defaultInvoiceCategories,
			deliveryStatus: state => state.deliveryStatus.deliveryStatus,
			users: state => state.users.users,
			isAdmin: state => state.auth.self.is_admin
		}),

		...mapGetters({
			getAccountById: 'configs/getAccountById',
			getMaterialById: 'materials/getMaterialById',
			getWarehouseById: 'warehouses/getById',
			getInvoiceCategoryById: 'invoiceCategories/getInvoiceCategoryById',
			isViewer: 'permissions/isViewer',
			getUserById: 'users/getUserById',
			getSettingByKey: 'settings/getSettingByKey'
		}),

		items() {
			const items = this.materials.filter(c => c.warehouse_id === this.tableInputs.warehouse);
			return this.tableInputs.warehouse ? items : this.materials;
		},

		headers() {
			let headers = [
				{
					text: this.$t('headers.no'),
					name: 'no',
					value: 'no',
					width: 40,
					noInput: true
				},
				{
					text: this.$t('headers.warehouse'),
					name: 'warehouse',
					value: 'warehouseId',
					setValueExpr: (val) => this.getWarehouseById(val).name,
					disableInput: this.tableDisabledInputs('warehouse')
				},
				{
					text: this.$t('headers.material'),
					name: 'materialId',
					value: 'materialId',
					setValueExpr: (val) => this.getMaterialById(val).name,
					inputRules: [this.rules.required],
					width: this.isWarehousePermission  ? '25%' : '',
					disableInput: this.tableDisabledInputs('material')
				},
				this.showMaterialSpecificationsColumn
					?{
						text: this.$t('headers.material-specifications'),
						name: 'material-specifications',
						value: 'material_specifications',
						type: 'string',
						width: 130,
						disableInput: true
					} 
					: null,
				this.useMaterialSerialNumber 
					? {
						text: this.$t('headers.serial-number'),
						name: 'serial-number',
						value: 'serial_number',
						inputRules:  [() => this.serialNumberValidation],
						width: 130,
						disableInput: this.tableDisabledInputs('serial-number'),
						type: 'array'
					}
					:null,
				{
					text: this.$t('headers.quantity'),
					name: 'quantity',
					value: 'quantity',
					type: 'number',
					width: 80,
					inputRules: [this.rules.required],
					disableInput: this.tableDisabledInputs('quantity')
				},
				...(!this.isWarehousePermission 
					? [
						{
							text: this.$t('headers.price'),
							name: 'price',
							value: 'price',
							type: 'number',
							width: 100,
							inputRules: !useZeroInvoiceEntiresPrice ? [this.rules.required] : [],
							disableInput: this.tableDisabledInputs('price')
						},
						{
							text: this.$t('headers.net-amount'),
							name: 'calculatedTotalAmount',
							value: 'calculated_total_amount',
							width: 100,
							disableInput: this.tableDisabledInputs('calculatedTotalAmount'),
							setInputExpr: (inputs, activeInput) => {
								if (activeInput === 'calculatedTotalAmount') {
										if(inputs.quantity) {
											this.tableInputs.price = inputs.calculatedTotalAmount / inputs.quantity;
											// this.$nextTick(() => {
											// 	this.tableInputs.calculatedTotalAmount = inputs.quantity * inputs.price;
											// })
										}

										return inputs.calculatedTotalAmount;
								} else if (['quantity', 'price'].includes(activeInput)) {
									return inputs.quantity * inputs.price;
								} else {
									return inputs.calculatedTotalAmount
								}
							},
						}
					]
					: []
				),
				{
					text: this.$t('headers.count'),
					name: 'count',
					type: 'number',
					width: 60,
					value: 'count',
					disableInput: this.tableDisabledInputs('count')
				},
				...(isMangeUnitAndRegionAndPatchAndLot 
					? [
						{
							text: 'رقم الباتش',
							name: 'patchNumber',
							value: 'patchNumber',
							disableInput: this.tableDisabledInputs('patch')
						},
						{
							text: 'رقم اللوت',
							name: 'lotNumber',
							value: 'lotNumber',
							disableInput: this.tableDisabledInputs('lot')
						},
						{
							text: 'تاريخ الانتهاء',
							name: 'endDate',
							value: 'endDate',
							type: 'date',
							disableInput: this.tableDisabledInputs('endDate')
						}
					]
					: []
				)
					,
				useMaterialStatusDeliver 
					? {
						text: this.$t('headers.delivery-status'),
						value: 'delivery_status_id',
						name: 'delivery_status_id',
						width: 120,
						noNotAvailableText: true,
						setValueExpr: val => this.deliveryStatus.find(c => c.id === val)?.name,
						disableInput: this.tableDisabledInputs('deliveryStatus')
					}
					: null,
				{
					text: this.$t('headers.notes'),
					name: 'notes',
					value: 'notes',
					width: this.isWarehousePermission  ? '25%' : '',
					disableInput: this.tableDisabledInputs('notes')
				},
				!this.postingPermission 
					? {
						text: '',
						name: 'hint',
						value: 'hint',
						width: 40,
						divider: false,
						class: 'px-0',
						cellClass: 'px-0',
						noInput: true,
						noNotAvailableText: true
					}
					: null
			]
			// user is Admin or accountant or viewer and you have permission for separationOfInvoices
			headers = headers.filter(v => Boolean(v));
			for (let i = 0; i < headers.length; i++) {
				if (i === headers.length - 1 && !this.postingPermission) {
					headers[i].class = headers[i].class ? headers[i].class + ' px-0' : 'px-0';
					headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-0' : 'px-0';
				} else {
					headers[i].class = headers[i].class ? headers[i].class + ' px-1 text-subtitle-2 ' : 'px-1 text-subtitle-2';
					headers[i].cellClass = headers[i].cellClass ? headers[i].cellClass + ' px-1 text-subtitle-2' : 'px-1 text-subtitle-2';
				}
				headers[i].align='center';
			}
			return headers;
		},
		serialNumberHeaders() {
			return [
				{
					name: 'id',
					setValueExpr: (v, i) => i+1 ,
					value: 'id',
					width: 30,
					disableInput: true,
					sortable: false
				},
				{
					name: 'value',
					value: 'value',
					type: 'string',
					text: this.$t('InvoiceBuyPage.serial-numbers'),
					sortable: false
				},
				{
					name: 'actions',
					text: '',
					value: '',
					noInput: true,
					width: 30,
					sortable: false,
					divider: false
				}
			]
		},
		postingPattern() {
        return +this.getSettingByKey('Posting Pattern')?.setting_value
		},
		adminPostingTypeActions() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst) {
				return {
					primaryButton: {
						color: !this.currentInvoice.posting_type? 'blueGray':'deletingColor',
						icon: !this.currentInvoice.posting_type? 'fa-envelope' : 'fa-envelope-open',
						text: !this.currentInvoice.posting_type
							? this.$t(`save-for-warehouse`)
							: this.currentInvoice.posting_type === postingType.materialsJournal
								? this.$t(`edit-for-warehouse`)
								: this.$t(`edit-for-accounting`),
						action: !this.currentInvoice.posting_type
							? () => {
								this.primaryPostingButtonLoading = true;
								this.changeInvoiceState(false,postingType.materialsJournal);
							}
							: this.currentInvoice.posting_type === postingType.materialsJournal
								? () => {
									this.primaryPostingButtonLoading = true;
									this.changeInvoiceState(true,postingType.materialsJournal) 
								}
								: () => {
									this.primaryPostingButtonLoading = true;
									this.changeInvoiceState(true,postingType.bond)
								}
					},
					secondaryButton: {
						color: this.currentInvoice.posting_type === postingType.both? 'deletingColor':'blueGray',
						icon: this.currentInvoice.posting_type === postingType.both? 'fa-envelope-open' : 'fa-envelope',
						text: !this.currentInvoice.posting_type
						? this.$t(`save-for-all`)
						: this.currentInvoice.posting_type === postingType.materialsJournal
							? this.$t(`save-for-accounting`)
							: this.$t(`edit-for-all`),
						action: !this.currentInvoice.posting_type
						? () => {
							this.secondaryPostingButtonLoading = true;
							this.changeInvoiceState(false,postingType.both)}
						: this.currentInvoice.posting_type === postingType.materialsJournal
							? () => {
								this.secondaryPostingButtonLoading = true;
								this.changeInvoiceState(false,postingType.bond) 
							}
							: () => {
								this.secondaryPostingButtonLoading = true;
								this.changeInvoiceState(true,postingType.both)
							}
					}
				}
			}
			if(this.postingPattern === postingPriorityType.accountantFirst) {
            return {
                primaryButton: {
                    color: !this.currentInvoice.posting_type? 'blueGray':'deletingColor',
                    icon: !this.currentInvoice.posting_type? 'fa-envelope' : 'fa-envelope-open',
                    text: !this.currentInvoice.posting_type
                        ? this.$t(`save-for-accounting`)
                        : this.currentInvoice.posting_type === postingType.bond
                            ? this.$t(`edit-for-accounting`)
                            : this.$t(`edit-for-warehouse`) ,
                    action: !this.currentInvoice.posting_type
                        ? () => {
                            this.primaryPostingButtonLoading = true;
                             this.changeInvoiceState(false,postingType.bond)
                        } 
                        : this.currentInvoice.posting_type === postingType.bond
                            ? () => {
                                this.primaryPostingButtonLoading = true;
                                this.changeInvoiceState(true,postingType.bond)
                            }
                            : () => {
                                this.primaryPostingButtonLoading = true;
                                this.changeInvoiceState(true,postingType.materialsJournal) 
                            }
                },
                secondaryButton: {
                    color: this.currentInvoice.posting_type === postingType.both? 'deletingColor':'blueGray',
                    icon: this.currentInvoice.posting_type === postingType.both? 'fa-envelope-open' : 'fa-envelope',
                    text: !this.currentInvoice.posting_type
                    ? this.$t(`save-for-all`)
                    : this.currentInvoice.posting_type === postingType.bond
                        ?  this.$t(`save-for-warehouse`)
                        : this.$t(`edit-for-all`),
                    action: !this.currentInvoice.posting_type
                    ?() => {
                            this.secondaryPostingButtonLoading = true;
                            this.changeInvoiceState(false,postingType.both)
                        } 
                    : this.currentInvoice.posting_type === postingType.bond
                        ? () => {
                            this.secondaryPostingButtonLoading = true;
                            this.changeInvoiceState(false,postingType.materialsJournal)
                        }
                        : () => {
                            this.secondaryPostingButtonLoading = true;this.changeInvoiceState(true,postingType.both)
                        }
                }
            }
        }
			return true;
		},
		adminPostingTypeAllowedActions() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst 
			&& this.isAdmin 
			&& this.currentInvoice.posting_type === postingType.materialsJournal) {
				return false;
			}
			return true ;
		},
		accountantPostingTypeAllowedActions() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst && this.isAccountantPermission)
				return false
			return true
		},
		warehousePostingTypeAllowedActions() {
        if(this.postingPattern === postingPriorityType.both && this.isWarehousePermission)
            return false;
        return true
		},
		postingPermission(){
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst) {
				if (this.isAccountantPermission) {
						return this.currentInvoice.posting_type === postingType.both
				}
				if (this.isWarehousePermission) {
						return this.currentInvoice.posting_type === postingType.materialsJournal ||
						this.currentInvoice.posting_type === postingType.both
				}
			}
			if(this.postingPattern === postingPriorityType.accountantFirst){
				if(this.isAccountantPermission) {
					return this.currentInvoice.posting_type === postingType.bond 
					|| this.currentInvoice.posting_type === postingType.both
				}
				if(this.isWarehousePermission) {
					return this.currentInvoice.posting_type === postingType.both
				}
			}
			return this.currentInvoice.is_posted;
		},
		userPostingTypeValue() {
			if(this.postingPattern === postingPriorityType.both)
				return 0;
			if (this.isAccountantPermission)
				return postingType.bond;
			if (this.isWarehousePermission)
				return postingType.materialsJournal;
			if(this.isAdmin)
				return postingType.both;
			return 0;
		},
		postingButtonStatus() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst) {
				if (this.isAccountantPermission) {
					return this.currentInvoice.posting_type !== postingType.unknown && this.currentInvoice.posting_type;
				}
				if (this.isWarehousePermission) {
					return this.currentInvoice.posting_type !== postingType.both;
				}
			}
			if(this.postingPattern === postingPriorityType.accountantFirst) {
				if(this.isWarehousePermission) {
					return this.currentInvoice.posting_type;
				}
				if(this.isAccountantPermission) {
					return this.currentInvoice.posting_type !== postingType.both
				}
			}
			return !this.isWarehousePermission;
		},
		deleteInvoicePermission() {
			if(!this.isAdmin && this.postingPermission) {
				return false;
			}
			if(this.postingPattern === postingPriorityType.both) {
				return !this.isWarehousePermission;
			}
			if (this.postingPattern === postingPriorityType.warehouseWorkersFirst) {
				return !this.isAccountantPermission;
			}
			return !this.isWarehousePermission;
		},
		createInvoicePermission() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst)
				return !this.isAccountantPermission
			if(this.postingPattern === postingPriorityType.accountantFirst)
				return !this.isWarehousePermission;
			return true
		},
		showAddRowPermission() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst) {
				if(this.isAccountantPermission && !this.currentInvoice.posting_type){
					return false;
				}
				return !this.currentInvoice.posting_type; 
			}
			if(this.postingPattern === postingPriorityType.accountantFirst) {
				if(this.isWarehousePermission && !this.currentInvoice?.posting_type)
					return false;
				return !this.currentInvoice.posting_type;
			}
			return !this.postingPermission;
		},
		deleteEntryPermission() {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst) {
				if(this.isAccountantPermission)
					return false;
				return !this.currentInvoice.posting_type;
			}
			if(this.postingPattern === postingPriorityType.accountantFirst) {
				if (this.isWarehousePermission)
					return false;
				return !this.currentInvoice.posting_type
			}
			return !this.currentInvoice.is_posted;
		},
		isWarehousePermission() {
			return (this.userTypePermission === userType.warehouseKeeper && separationOfInvoices) || (this.userTypePermission === userType.warehouseManger && separationOfInvoices)
    	},
		isAccountantPermission() {
        return (this.userTypePermission === userPermission.accountant && separationOfInvoices) || 
            (this.userTypePermission === userPermission.advanceAccountant && separationOfInvoices)
		},
		contextMenuItems() {
			const items = [			
				{
					title: this.$t('buttons.edit'),
					event: 'edit',
				}	
			]
			if(this.deleteEntryPermission) {
					items.push({
					title: this.$t('buttons.delete'),
					event: 'delete',
					})
			}
			return items;
		},

		inputsValidationMessages() {
			const messages = [];
			const inputs = this.tableInputs;
			if (!inputs.materialId) {
				messages.push(this.$t('table-messages.material-required'));
			}
			if (!inputs.quantity) {
				messages.push(this.$t('table-messages.quantity-required'));
			}
			if (!inputs.price) {
				messages.push(this.$t('table-messages.price-required'));
			}

			return messages
		},

		bond_posting_ratio() {
			return this.numOfTotalSaleRefundInvoices.length
				? 100 -
				((this.numOfNotPostedInvoices.length ? this.numOfNotPostedInvoices[0].count : 0) * 100) /
					this.numOfTotalSaleRefundInvoices[0].count
			: 100   
    	},

		bond_posting_ratio_tooltip() {
			return this.numOfNotPostedInvoices.length
				? `${this.numOfNotPostedInvoices[0].count} ${this.$t('unposted-invoices')}`
				: this.$t('unposted-invoices');
		},
		bond_posting_ratio_caption() {
			return `${this.numOfNotPostedInvoices.length ? this.numOfNotPostedInvoices[0].count : 0}/${this.numOfTotalSaleRefundInvoices.length ? this.numOfTotalSaleRefundInvoices[0].count : 0}`;
		},

		tradeConfigAccounts() {
			return [
				{ 
					...(
						this.getAccountById(this.defaultFinancialFileAccounts.purchases_refund_account_id) || {}
					),
					id: null
				},
				...this.configs.accounts,
			].filter(c =>
				( this.accountFinalTypeRestrict ? c.final_type === finalType.trade : true) &&
				c.type === type.sub &&
				c.id !== this.defaultFinancialFileAccounts.purchases_refund_account_id
			)
		},

		allInvoiceCategories() {
			return [
				{ id: null, name: this.$t('InvoiceSalePage.all-categories') } ,
				...this.invoiceCategories
			]
		},

		matchedCategoryInvoices() {
			if(this.selectedInvoiceCategory) {
				return this.buyRefundInvoices.filter(c => c.category_id === this.selectedInvoiceCategory)
			} else {
				return this.buyRefundInvoices
			}
		},

		infoHeaders() {
			const headers = [
				{
					text: this.$t('invoice-headers.action'),
					value: 'type',
					name: 'type',
					sortable: false
				},
				{
					text: this.$t('invoice-headers.date-formatted'),
					value: 'date_formatted',
					name: 'date',
					sortable: false
				},
				{
					text: this.$t('invoice-headers.user'),
					value: 'user_id',
					setValueExpr: value => this.getUserById(value)?.user_name,
					name: 'userName',
					sortable: false
				}
			]
			return headers
		},

		totalQuantities() {
			return this.entries?.reduce((sum, item) => {
				return sum + item.quantity
			}, 0)
		},

		totalCounts() {
			return this.entries?.reduce((sum, item) => {
				return sum + item?.count
			}, 0)
		},
		currencyNameCreate() {
			if (this.submitData.alternative_currency_id) {
				return this.$t('change-currency.currency') + ': ' + this.configs.currencies.find(c => c.id === this.submitData.alternative_currency_id)?.name
			}
			return this.$t('change-currency.currency')
		},

		currencyNameUpdate() {
			if (this.invoiceData.alternativeCurrencyId) {
				return this.$t('change-currency.currency') + ': ' + this.configs.currencies.find(c => c.id === this.invoiceData.alternativeCurrencyId)?.name
			}
			return this.$t('change-currency.currency')
		},

		disableDefaultCurrencyPrice() {
			return Boolean(this.invoiceData.alternativeCurrencyId === this.currentFinancialFile.default_currency_id)
		},

		disableDefaultCurrencyPriceCreate() {
			return Boolean(this.submitData.alternative_currency_id === this.currentFinancialFile.default_currency_id)
		},
		serialNumberValidation() {
			return  this.tableInputs['serial-number']?.length === this.tableInputs.quantity ||  !this.getMaterialById(this.tableInputs.materialId)?.is_required_serial_num
		},
		/*settings*/
		canAddFutureDate() {
			return this.getSettingByKey('CanAddFutureDate')?.setting_value;
		},
		showMaterialSpecificationsColumn() {
			return this.getSettingByKey('ShowMaterialSpecifications')?.setting_value;
		},
		useMaterialSerialNumber() {
			return this.getSettingByKey('UseMaterialSerialNumber')?.setting_value;
		},
		useDublicateMaterialSerialNumberAlert() {
			return this.getSettingByKey('DuplicateSerialNumAlert')?.setting_value;
		},
		accountFinalTypeRestrict() {
			return this.getSettingByKey('ValidateAccountType')?.setting_value
		}
	},

	methods: {
		invoiceNoIsChanged() {
			const newInvoice =
				this.buyRefundInvoices.find(c => c[!this.$route.query.invoiceCategory ? 'no' : 'sub_no'] === this.invoiceNo);
			if (newInvoice && newInvoice.id !== +this.$route.params.id) {
				this.$router.replace({
					name: this.$route.name,
					params: { id: newInvoice.id },
					query: this.$route.query
				})
			}
		},

		// submit data
		submitInvoice() {
			if (
                (
                    (
                        this.invoiceData.alternativeCurrencyId !== null 
                        && this.invoiceData.alternativeCurrencyId !== this.currentFinancialFile.default_currency_id
                    ) 
                    && this.currentInvoice.alternative_currency_id !== null
                ) 
                && this.invoiceData.alternativeCurrencyId != this.currentInvoice.alternative_currency_id
            ) 
            {
				this.changeCurrencyAlertDialog = true
				this.invoiceData.alternativeCurrencyId = this.currentInvoice.alternative_currency_id;
				this.recommendCurrencyDefaultPriceUpdate(this.invoiceData.alternativeCurrencyId);
				return null;
			}
			if (!this.loading && !this.submitLoading && (this.isUpdateInvoice || this.$refs.form.validate())  && (!this.isUpdateInvoice || this.invoiceData.date <= moment().format('YYYY-MM-DD') || this.canAddFutureDate)) {
				this.changeCurrencyLoading = true;
				this.submitLoading = true;
				/*fill the category autocomplete*/
				const actionName = !this.isUpdateInvoice ? 'create' : 'update';
				this.$store.dispatch(`invoices/${actionName}`, {
					invoiceId: this.$route.params.id,
					invoiceType: invoiceType.buyRefund,
					invoice: {
						external_account_id:
							!this.isUpdateInvoice
								? this.submitData.account
								: this.invoiceData.accountId,
						date:
							!this.isUpdateInvoice
								? this.submitData.date
								: this.invoiceData.date,
						doc_no:
							!this.isUpdateInvoice
								? this.submitData.refDoc
								: this.invoiceData.refDoc,
						notes:
							!this.isUpdateInvoice
								? this.submitData.notes
								: this.invoiceData.notes,
						refunded_from_invoices:
							!this.isUpdateInvoice
								? this.submitData.refundedFrom
								: this.invoiceData.refundedFrom,
						account_trade_config_id:
							!this.isUpdateInvoice
								? this.submitData.account_trade_config_id
								: this.invoiceData.account_trade_config_id,
						category_id: 
							!this.isUpdateInvoice
								? this.submitData.categoryId
								: this.invoiceData.categoryId,
						alternative_currency_id:
							!this.isUpdateInvoice
								? !(this.submitData.alternative_currency_id === this.configs.currencies.find(c => c.id === this.currentFinancialFile.default_currency_id).id)
									? this.submitData.alternative_currency_id
									: null
								:!(this.invoiceData.alternativeCurrencyId === this.configs.currencies.find(c => c.id === this.currentFinancialFile.default_currency_id).id)
									? this.invoiceData.alternativeCurrencyId
									: null,
						currency_price :
							!this.isUpdateInvoice
								? !(this.submitData.alternative_currency_id === this.configs.currencies.find(c => c.id === this.currentFinancialFile.default_currency_id).id)
									? Number(this.submitData.currency_price)
									: null
								:!(this.invoiceData.alternativeCurrencyId === this.configs.currencies.find(c => c.id === this.currentFinancialFile.default_currency_id).id)
									? Number(this.invoiceData.alternativeCurrencyPrice)
									: null,
						total_net: 
							this.isUpdateInvoice 
							? (this.invoiceData.alternativeCurrencyId === this.configs.currencies.find(c => c.id === this.currentFinancialFile.default_currency_id).id)
								? { amount: this.invoiceData.total_net_amount || 0, currency_id: this.configs.financial_file.default_currency_id }
								: { amount: 0, currency_id: this.configs.financial_file.default_currency_id }
							: null
						,
						alternative_total_net: 
							this.isUpdateInvoice && !(this.invoiceData.alternativeCurrencyId === this.configs.currencies.find(c => c.id === this.currentFinancialFile.default_currency_id).id) 
							? { amount: this.invoiceData.total_net_amount || 0, currency_id: this.invoiceData.alternativeCurrencyId}
							: null
						
					}
				}).then(async () => {
					await this.$store.dispatch('invoices/fetchAll', {invoiceType: this.invoiceType.buyRefund})
						.then((invoices) => {
							/*fill the category autocomplete*/
							this.selectedInvoiceCategory = this.submitData.categoryId;
							const data =
								useInvoiceCategories && this.matchedCategoryInvoices.length
									? this.matchedCategoryInvoices
									: invoices;
							if (data[data.length - 1].id !== +this.$route.params.id && !this.isUpdateInvoice) {
								this.$router.replace({
									name: this.$route.name,
									params: { id: data[data.length - 1].id },
									query: {
										...this.$route.query,
										invoiceCategory: this.submitData.categoryId
									}
								})
							} else if (this.isUpdateInvoice) {
								this.fetchInvoice();
								EventBus.$emit("notification", this.$t('AllInvoicesPage.invoice-updated-successfully'));
							}
						})
						.finally(() => {
							this.dialog = false;
							this.submitLoading = false;	
							this.changeCurrencyLoading = false;
						})
					
					if (this.isUpdateInvoice) this.isUpdateInvoice = false;
					else this.$store.dispatch("configs/fetchAll")
				}).catch(() => {
					this.submitLoading = false;
				})
			}
		},

		async changeInvoiceState(postingPermission = this.postingPermission, postingTypeValue = this.userPostingTypeValue) {
			if (!postingPermission && this.showAddRowPermission) {
				try {
					await this.saveEntry()
				}
				catch (error) {
					console.error(error)
				}	
			}
			const actionName = !postingPermission ? 'updateToPosted' : 'updateToNotPosted'
			
			this.submitLoading = 
			this.loading = true;
			this.$store.dispatch(`invoices/${actionName}`, {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.buyRefund,
				posting_type: postingTypeValue
			}).then(() => {
				// update journals, bonds and configs
				this.fetchInvoice().finally(() => {
					this.loading = false;
					this.submitLoading = false;
					this.primaryPostingButtonLoading  = false;
					this.secondaryPostingButtonLoading = false;
				})
				if(postingTypeValue !== postingType.bond) {
					this.$store.dispatch("materialsJournals/fetchAll", { fromDate: this.configs.financial_file.start_date, toDate: new Date().toISOString().substr(0, 10) }),
					this.fetchMaterials()
				}
				if(postingTypeValue !== postingType.materialsJournal) {
					this.$store.dispatch('bonds/fetchAll', { bondType: bondType.accounting })
					this.$store.dispatch('configs/fetchAll')
				}
			}).catch(() => {
				this.loading = false;
				this.submitLoading = false;
				this.primaryPostingButtonLoading  = false;
				this.secondaryPostingButtonLoading = false;
				this.confirmPostDialog = false
			})
		},

		async saveEntry() {
			await this.$nextTick(() => {});
			if(this.useMaterialSerialNumber) {
					this.serialNumber.value
                            ? Array.isArray(this.tableInputs['serial-number']) 
                                ? this.tableInputs['serial-number'].push(this.serialNumber) 
                                : this.tableInputs['serial-number'] = [this.serialNumber]
                            : null
                    this.serialNumber = {}
			}
			if (this.$refs.table.validate()) {
				const actionName = this.entryId ? 'updateEntry' : 'createEntry';
				const material = this.getMaterialById(this.tableInputs.materialId);
				
				const entry = this.entries.find(c => c.id === this.entryId);
				return this.$store.dispatch(`invoices/${actionName}`, {
					invoiceType: invoiceType.buyRefund,
					invoiceId: this.$route.params.id,
					invoiceEntry: {
						id: this.entryId || 0,
						invoice_id: this.$route.params.id,
						material_id: material.id,
						no: entry && entry.no ? entry.no : 0,
						quantity: {
							quantity: this.tableInputs.quantity,
							unit_size_id: material.unit_size_id
						},
						count: this.tableInputs.count || 0,
						price: this.currentInvoice.alternative_currency_id
							? {
								amount: 0,
								currency_id: this.currentFinancialFile.default_currency_id
							}
							: {
								amount: this.tableInputs.price ? this.tableInputs.price : 0,
								currency_id: this.currentFinancialFile.default_currency_id
							},
						alternative_price: this.currentInvoice.alternative_currency_id
							? {
								amount: this.tableInputs.price ? this.tableInputs.price : 0,
								currency_id: this.currentInvoice.alternative_currency_id
							}
							: {
								amount: 0,
								currency_id: this.currentFinancialFile.default_currency_id
							},
						calculated_total_amount: this.currentInvoice.alternative_currency_id
							? {
								amount: 0,
								currency_id: this.currentFinancialFile.default_currency_id
							}
							: {
								amount: this.tableInputs.price ? this.tableInputs.quantity * this.tableInputs.price : 0,
								currency_id: this.currentFinancialFile.default_currency_id
						},
						alternative_calculated_total_amount: this.currentInvoice.alternative_currency_id
							? {
								amount: this.tableInputs.price ? this.tableInputs.quantity * this.tableInputs.price : 0,
								currency_id: this.currentInvoice.alternative_currency_id
							}
							: {
								amount: 0,
								currency_id: this.currentFinancialFile.default_currency_id
							},
						notes: this.tableInputs.notes,
						delivery_status_id: this.tableInputs.delivery_status_id,
						serial_number: Array.isArray(this.tableInputs['serial-number']) ? this.tableInputs['serial-number'].map(v => v.value) : []
						// patchNumber: this.tableInputs.patchNumber
						// lotNumber: this.tableInputs.lotNumber
						// endDate: this.tableInputs.endDate
					}
				}).then(() => {
					const initTable = () => {
						this.$nextTick(() => {
							this.$refs.table.scrollToRow();
							this.$refs.table.resetInputs({
								warehouse: this.configs.warehouse.id
							});
						})
					}
					
					if (this.entryId) {
						this.entryId = null;
						const nextRowIndex = this.$refs.table.editNextRow();
						if (nextRowIndex !== -1) {
							this.entryId = this.entries[nextRowIndex].id;
							this.$refs.table.scrollToRow();
						} else {
							initTable();
						}
					} else {
						initTable();
					}

					this.$nextTick(() => {
						if (this.entryId) setTimeout(this.$refs.table.resetInputsFocus, 100);
						else this.$refs.table.resetInputsFocus();
					})

					this.fetchInvoice();
				})
			}
		},
		fetchMaterials(isUpdate = true) {
			this.materialsLoading = true;
			return this.$store.dispatch('materials/fetchAll').finally(() => {
				this.materialsLoading = false;
				if(isUpdate) {
					EventBus.$emit('notification', this.$t('update-materials-notifications'))
				}
			})
		},
		setRowIndex(rowData) {
			this.rowEditIndex = rowData.index;
			this.entryId = rowData.item.id;
			this.$nextTick(() => {
				this.$refs.table.resetInputsFocus()
			})
		},

		fetchInvoice() {
			this.loading = true;
			return this.$store.dispatch('invoices/fetch', {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.buyRefund
			}).then((data) => {
				if(this.$route.query.withCategory) {
                    this.$router.push({name: 'buy-invoice-refund', params: {id: this.$route.params.id}, query: {invoiceCategory: data.category_id} })
                }
				this.currentInvoice = data;
				
				this.invoiceNo = !this.$route.query.invoiceCategory ? data.no : data.sub_no;
				this.invoiceData.accountId = data.external_account_id;
				this.invoiceData.date = data.date.slice(0, 10);
				this.invoiceData.refDoc = data.doc_no;
				this.invoiceData.notes = data.notes;
				this.invoiceData.refundedFrom = data.refunded_from_invoices;
				this.invoiceData.account_trade_config_id = data.account_trade_config_id
				this.invoiceData.categoryId = data.category_id

				this.entries = data.invoice_entries.map(c => {
					const material = this.getMaterialById(c.material_id);
					return ({
						...c,
						no: c.no,
						warehouseId: material?.warehouse_id,
						materialId: c.material_id,
						material_specifications: material?.specifications,
						quantity: c.quantity.quantity,
						count: c.count,
						price: data.alternative_currency_id
							? c.alternative_price.amount 
							: c.price.amount,
						calculated_total_amount: data.alternative_currency_id
							? c.alternative_calculated_total_amount.amount 
							: c.calculated_total_amount.amount,
						notes: c.notes,
						delivery_status_id: c.delivery_status_id,
						serial_number: c.serial_number?.map((v,i) => ({value: v, id: i + 1}))
					})
				})
				if (data.alternative_currency_id === null) {
                    this.invoiceData.alternativeCurrencyId = this.currentFinancialFile.default_currency_id;
                    this.invoiceData.alternativeCurrencyPrice = 1
                } else {
                    this.invoiceData.alternativeCurrencyId = data.alternative_currency_id
                    this.recommendCurrencyPriceUpdate(this.invoiceData.alternativeCurrencyId)
                }
				this.invoiceData.calculated_total_amount = this.entries
																? this.entries
																	.map(c => c.calculated_total_amount || 0 )
																	.reduce((a, b) => a + b, 0)
																: null;
				this.invoiceData.total_net_amount = this.invoiceData.calculated_total_amount;
				this.currentCurrencyDialog = false
				this.$nextTick(() => {
					if(!this.postingPermission){
						this.$refs.table.scrollToRow();
					}
				})

				this.auditLogEntries = data.audit_log_simplified_responses
			}).finally(() => {
				this.isUpdateInvoice = false;
				this.loading = false;
			})
		},

		fetchInvoices() {
			return this.$store.dispatch('invoices/fetchAll', { invoiceType: this.invoiceType.buyRefund }).then((invoices) => {
				const data =
					useInvoiceCategories && this.matchedCategoryInvoices.length
						? this.matchedCategoryInvoices
						: invoices;

				if (!this.matchedCategoryInvoices.length) {
					this.selectedInvoiceCategory = null;
				}
				if (data.length && !this.$route.params.id && this.$route.name === 'buy-invoice-refund') {
					this.$router.replace({
						name: this.$route.name,
						params: { id: data[data.length - 1].id },
						query: this.matchedCategoryInvoices.length ? this.$route.query : {}
					})
				} else if (data.length && data.find(c => c.id === +this.$route.params.id)) {
					this.fetchInvoice();
				} else {
				this.invoiceNo = null;
				this.invoiceData.accountId = null;
				this.invoiceData.date = null;
				this.invoiceData.notes = null;
				this.invoiceData.refDoc = null;
				this.invoiceData.refundedFrom = [];
				this.loading = false;
				}
			})
		},

		deleteEntry() {
			this.loading = true;
			this.$store.dispatch('invoices/deleteEntry', {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.buyRefund,
				invoiceEntryId: this.deleteEntryId
			}).then(() => {
				this.deleteEntryId = null;
				this.fetchInvoice();
			})
			this.$refs.table.resetEdit();
		},

		deleteInvoice() {
			this.loading = true;

			this.$store.dispatch('invoices/delete', {
				invoiceId: this.$route.params.id,
				invoiceType: invoiceType.buyRefund
			}).then(() => {
				this.fetchInvoices().then(() => {
					this.$router.replace({
						name:'buy-invoice-refund',
						params:
							this.matchedCategoryInvoices.length
								? { id: this.matchedCategoryInvoices[this.matchedCategoryInvoices.length - 1].id }
								: {},
						query: this.$route.query
					});
				})
			}).catch(() => {
				this.loading = false;
			})
		},

		suggestCategoryData() {
			if (!useInvoiceCategories) return;
			let matchedCategory = this.getInvoiceCategoryById(this.submitData.categoryId);
			if(matchedCategory) {
				this.submitData.account_trade_config_id = matchedCategory?.sales_refund_account_id;
				this.submitData.account = matchedCategory?.customer_account_id;
			}
		},

		getReport(type) {
			this.$store.dispatch('reports/invoice', {
				type,
				financialFileId: this.currentFinancialFile.id,
				userId: ls.getLocalUser().id,
				invoiceType: invoiceType.buy,
				invoiceId: this.$route.params.id,
				withDeliveryStatus: this.useMaterialStatusDeliver
			})
		},

		tableDisabledInputs(input) {
			if(this.postingPattern === postingPriorityType.warehouseWorkersFirst){
				if(this.isAccountantPermission && this.currentInvoice.posting_type === postingType.unknown)
					return input !== 'price' && input !== 'calculatedTotalAmount';
				if(this.currentInvoice.posting_type === postingType.materialsJournal)
					return input !== 'price' && input !== 'calculatedTotalAmount';
			}
			if(this.postingPattern === postingPriorityType.accountantFirst) {
				if(this.currentInvoice.posting_type === postingType.bond) 
					return input !== 'notes'
			}
			return false;
		},
		// recommendMaterialPrice() {
			
		// },
		recommendMaterialData() {
				this.tableInputs['serial-number'] = [];
				const material = this.materials.find(c => c.id === this.tableInputs.materialId)
				this.tableInputs.price = material?.purchase_price?.amount / (this.currentInvoice.currency_price || 1);
				this.tableInputs['material-specifications'] = material?.specifications;
				if(this.useMaterialSerialNumber) {
					this.getMaterialSerialNumbers(this.tableInputs.materialId);
				}
		},
		getMaterialSerialNumbers(materialId) {
			this.materialSerialNumbers = [];
			this.$store.dispatch('materials/getMaterialSerialNumbers', {materialId, invoiceType: invoiceType.buyRefund}).then(data => {
				this.materialSerialNumbers = data;
				// this.materialSerialNumbers.push('111')
			})
		},
		resetCurrencyDialog() {
			if (this.currentInvoice.alternative_currency_id === null) {
				this.invoiceData.alternativeCurrencyId = this.currentFinancialFile.default_currency_id
				this.invoiceData.alternativeCurrencyPrice = 1
			} else {
				this.invoiceData.alternativeCurrencyId = this.currentInvoice.alternative_currency_id
				this.invoiceData.alternativeCurrencyPrice = this.currentInvoice.currency_price
			}
		},
		recommendCurrencyPriceCreate(value) {
			if (value === this.currentFinancialFile.default_currency_id) {
				this.submitData.currency_price = 1
			} else {
				this.submitData.currency_price = this.configs.currencies.find(e => e.id === value).latest_exchange_record
			}
		},

		recommendCurrencyDefaultPriceUpdate(value) {
			if (value === this.currentFinancialFile.default_currency_id) {
				this.invoiceData.alternativeCurrencyPrice = 1
			} else {
				this.invoiceData.alternativeCurrencyPrice = this.configs.currencies.find(e => e.id === value).latest_exchange_record
			}
		},
		recommendCurrencyPriceUpdate(value) {
			if(this.currentInvoice.alternative_currency_id === value) {
				this.invoiceData.alternativeCurrencyPrice = this.currentInvoice.currency_price
			} else {
				this.invoiceData.alternativeCurrencyPrice = this.configs.currencies.find(e => e.id === value).latest_exchange_record
			}
		},
		checkMaterialSerialNumber(materialId, serialNumber) { 
			if(materialId && serialNumber && this.useDublicateMaterialSerialNumberAlert) {
				this.$store.dispatch('materials/checkSerialNumber', {materialId, serialNumber}).then(data => {
					if(data) {
						this.serialNumberAlertDialog = true;
						this.serialNumberAlertText = serialNumber;
					}
				})
			}
		},
		resetSerialNumberValue() {
			setTimeout(() => {
				const serialNumber = this.serialNumber;
				if(serialNumber.value)
				{
					Array.isArray(this.tableInputs['serial-number']) 
						? this.tableInputs['serial-number'].push(serialNumber) 
						: this.tableInputs['serial-number'] = [serialNumber]
				}
					this.serialNumber = {};
				if(!this.addSerialNumbersDialog) {
					this.$refs['input-serial-number'].activateMenu()
				} else {
					/*to stop opening the combobox menu*/
					this.$refs['input-value'].isMenuActive = false
				}
			})
		}
	},

	created() {
		this.selectedInvoiceCategory = this.$route.query.invoiceCategory || null;
		this.submitData.alternative_currency_id = this.currentFinancialFile.default_currency_id
		this.submitData.currency_price = 1
		this.loading = true;
		Promise.all([
			!this.materials.length ? this.fetchMaterials(false) : null,
			this.$store.dispatch('warehouses/fetchAll'),
			this.$store.dispatch('invoices/fetchAll', { invoiceType: this.invoiceType.buy }),
			useConfigInvoicesAccount && !Object.keys(this.defaultFinancialFileAccounts).length
				? this.$store.dispatch('configs/fetchDefaultFinancialFileAccounts')
				: null,
			!this.invoiceCategories.length && useInvoiceCategories? this.$store.dispatch('invoiceCategories/fetchAll') : null,
			!this.defaultInvoiceCategories.length && useConfigInvoicesAccount ? this.$store.dispatch('invoiceCategories/fetchDefaultCategories') : null,
		]).then(() => {
			this.fetchInvoices().finally(() => {
				this.tableInputs.warehouse = this.configs.warehouse.id;
			});
			!(this.deliveryStatus.length > 0) ? this.$store.dispatch('deliveryStatus/fetchAll') : null
		}).catch(() => {
			this.loading = false;
		});
		
		!(this.deliveryStatus.length > 0) ? this.$store.dispatch('deliveryStatus/fetchAll') : null
		!this.users.length ? this.$store.dispatch('users/fetchAll') : null
	}
}
</script>
